import React, { useState } from 'react';
import { X } from 'lucide-react';

interface EmojiPickerProps {
  onEmojiSelect: (emoji: string) => void;
  onClose: () => void;
}

const EmojiPicker: React.FC<EmojiPickerProps> = ({ onEmojiSelect, onClose }) => {
  const [activeCategory, setActiveCategory] = useState('status');

  const emojiCategories = {
    status: {
      name: 'Status',
      emojis: [
        '🟢', '🔴', '🟡', '🟠', '🔵', '🟣', '⚫', '⚪',
        '✅', '❌', '⚠️', '🚫', '🔥', '💧', '⭐', '💎',
        '🎯', '🏆', '🎉', '🎊', '💯', '🔔', '🔕', '📢'
      ]
    },
    symbols: {
      name: 'Symbols',
      emojis: [
        '➡️', '⬅️', '⬆️', '⬇️', '↗️', '↘️', '↙️', '↖️',
        '🔄', '🔃', '🔁', '🔂', '⏸️', '⏯️', '⏹️', '⏺️',
        '⏭️', '⏮️', '⏪', '⏩', '🔀', '🔁', '🔂', '🔄'
      ]
    },
    activities: {
      name: 'Activities',
      emojis: [
        '🏃', '🚶', '🧘', '🛌', '💼', '🎯', '🎮', '📚',
        '💻', '📱', '🖥️', '⌨️', '🖱️', '🖨️', '📞', '📧',
        '🔧', '🔨', '⚙️', '🛠️', '🔩', '⚡', '🔋', '🔌'
      ]
    },
    objects: {
      name: 'Objects',
      emojis: [
        '🏠', '🏢', '🏭', '🏪', '🏫', '🏥', '🏦', '🏨',
        '🚗', '🚕', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑',
        '📦', '📋', '📊', '📈', '📉', '💰', '💳', '🎁'
      ]
    },
    nature: {
      name: 'Nature',
      emojis: [
        '☀️', '🌙', '⭐', '🌟', '💫', '✨', '☁️', '⛅',
        '🌈', '🔥', '💧', '❄️', '⚡', '🌊', '🌍', '🌎',
        '🌱', '🌿', '🍀', '🌸', '🌺', '🌻', '🌹', '🌷'
      ]
    }
  };

  return (
    <div className="bg-white border border-slate-300 rounded-lg shadow-xl p-3 w-64">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-sm font-medium text-slate-700">Select Emoji</h3>
        <button
          onClick={onClose}
          className="p-1 hover:bg-slate-100 rounded transition-colors"
        >
          <X className="w-4 h-4 text-slate-500" />
        </button>
      </div>

      {/* Category Tabs */}
      <div className="flex gap-1 mb-3 overflow-x-auto">
        {Object.entries(emojiCategories).map(([key, category]) => (
          <button
            key={key}
            onClick={() => setActiveCategory(key)}
            className={`px-2 py-1 text-xs rounded transition-colors whitespace-nowrap ${
              activeCategory === key
                ? 'bg-blue-500 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {category.name}
          </button>
        ))}
      </div>

      {/* Emoji Grid */}
      <div className="grid grid-cols-8 gap-1 max-h-48 overflow-y-auto">
        {emojiCategories[activeCategory as keyof typeof emojiCategories].emojis.map((emoji, index) => (
          <button
            key={index}
            onClick={() => onEmojiSelect(emoji)}
            className="w-6 h-6 flex items-center justify-center text-lg hover:bg-slate-100 rounded transition-colors"
            title={emoji}
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  );
};

export default EmojiPicker;